import React, { useState } from 'react';
import { useAdminAccess } from '../hooks/useAdminAccess';
import { ChangelogManagementPanel } from '../components/ChangelogManagementPanel';
import { BetaStatusEditor } from '../components/BetaStatusEditor';
import { LegalDocumentEditor } from '../components/LegalDocumentEditor';
import { UserManagementPanel } from '../components/UserManagementPanel';
import { ContentModerationPanel } from '../components/ContentModerationPanel';
import { MFAEnrollment } from '../components/MFAEnrollment';
import { MFAChallenge } from '../components/MFAChallenge';
import { EventManagementPanel } from '../components/EventManagementPanel';
import { useTheme } from '../context/ThemeContext';
import AdminWorkspace from './admin/AdminWorkspace';
import OperationsPanel from './admin/OperationsPanel';
import WaitlistPanel from './admin/WaitlistPanel';
import { PublicationConfirm, WaitlistDeleteDialogs } from './admin/ConfirmationDialogs';
import { useAdminSession } from './admin/useAdminSession';
import { useWaitlistOperations } from './admin/useWaitlistOperations';
import { AdminTab } from './admin/adminTypes';

const AdminConsole: React.FC = () => {
  const { isAuthorized, isLoading, userEmail } = useAdminAccess();
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [notification, setNotification] = useState<{message: string, type: 'error' | 'success' | 'warning'} | null>(null);

  const session = useAdminSession(isAuthorized, userEmail, setNotification);
  const waitlist = useWaitlistOperations(
    isAuthorized && session.mfaState === 'verified',
    isLoading,
    setNotification
  );

  const {
    logout, mfaState, setMfaState, retryMfaCheck,
    appPublished, appPublishedLoading, appPublishedConfirm, setAppPublishedConfirm,
    androidPublished, androidPublishedLoading, androidPublishedConfirm, setAndroidPublishedConfirm,
    handleToggleAppPublished, handleToggleAndroidPublished,
  } = session;

  const {
    entries, loading, showLogsInfo, setShowLogsInfo, deleteConfirm, setDeleteConfirm,
    selectedEntries, setSelectedEntries, bulkDeleteConfirm, setBulkDeleteConfirm,
    resendingEmail, logs, logsLoading, feedback, feedbackLoading, reasonCounts,
    intradayCount, fetchEntries, deleteEntry, bulkDeleteEntries, toggleEntrySelection,
    selectAllEntries, resendWelcomeEmail, exportToCSV, fetchLogs, fetchFeedback, formatDate,
  } = waitlist;

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: colors.background }}
      >
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 rounded-full mx-auto mb-4" style={{ borderColor: 'var(--rp-border)', borderTopColor: 'var(--rp-text-secondary)' }}></div>
          <p style={{ color: 'var(--rp-text-secondary)' }}>Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return null;
  }

  if (mfaState === 'loading') {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: colors.background }}
      >
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 rounded-full mx-auto mb-4" style={{ borderColor: 'var(--rp-border)', borderTopColor: 'var(--rp-text-secondary)' }}></div>
          <p style={{ color: 'var(--rp-text-secondary)' }}>Verifying two-factor status...</p>
        </div>
      </div>
    );
  }

  if (mfaState === 'error') {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: colors.background }}
      >
        <div className="text-center">
          <p className="mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
            We could not verify two-factor status. The workspace stays locked until verification succeeds.
          </p>
          <button
            type="button"
            onClick={retryMfaCheck}
            className="admin-btn px-4 py-2 rounded-lg text-sm font-semibold mr-3"
            style={{ color: 'var(--rp-text)', border: '1px solid var(--rp-border)' }}
          >
            Retry
          </button>
          <button
            type="button"
            onClick={async () => { await logout(); window.location.reload(); }}
            className="admin-btn px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ color: 'var(--rp-text)', border: '1px solid var(--rp-border)' }}
          >
            Log out
          </button>
        </div>
      </div>
    );
  }

  if (mfaState === 'challenge') {
    return <MFAChallenge onVerified={() => setMfaState('verified')} onLogout={async () => { await logout(); window.location.reload(); }} />;
  }

  // Enrollment is mandatory: an account that has not completed 2FA never sees
  // the privileged workspace. 'Back to sign in' signs out rather than skipping.
  if (mfaState === 'enroll') {
    return (
      <MFAEnrollment
        required
        onComplete={() => setMfaState('verified')}
        onSkip={async () => { await logout(); window.location.reload(); }}
      />
    );
  }

  return (
    <AdminWorkspace
      activeTab={activeTab}
      onTabChange={setActiveTab}
      userEmail={userEmail}
      onLogout={logout}
      notification={notification}
      onDismissNotification={() => setNotification(null)}
    >
      {activeTab === 'dashboard' && (
        <OperationsPanel
          appPublished={appPublished}
          appPublishedLoading={appPublishedLoading}
          onRequestToggleApp={() => setAppPublishedConfirm(true)}
          androidPublished={androidPublished}
          androidPublishedLoading={androidPublishedLoading}
          onRequestToggleAndroid={() => setAndroidPublishedConfirm(true)}
          logs={logs}
          logsLoading={logsLoading}
          showLogsInfo={showLogsInfo}
          onToggleLogsInfo={() => setShowLogsInfo(!showLogsInfo)}
          onRefreshLogs={fetchLogs}
        />
      )}
      {activeTab === 'waitlist' && (
        <WaitlistPanel
          intradayCount={intradayCount}
          loading={loading}
          entries={entries}
          selectedEntries={selectedEntries}
          resendingEmail={resendingEmail}
          onSelectAll={selectAllEntries}
          onToggleEntry={toggleEntrySelection}
          onClearSelection={() => setSelectedEntries(new Set())}
          onRequestBulkDelete={() => setBulkDeleteConfirm(true)}
          onResendEmail={resendWelcomeEmail}
          onRequestDelete={setDeleteConfirm}
          onRefresh={fetchEntries}
          onExportCSV={exportToCSV}
          feedbackLoading={feedbackLoading}
          onRefreshFeedback={fetchFeedback}
          reasonCounts={reasonCounts}
          feedback={feedback}
          formatDate={formatDate}
        />
      )}
      {activeTab === 'users' && <UserManagementPanel />}
      {activeTab === 'moderation' && <ContentModerationPanel />}
      {activeTab === 'events' && <EventManagementPanel />}
      {activeTab === 'legal' && <LegalDocumentEditor />}
      {activeTab === 'changelog' && (
        <>
          <BetaStatusEditor />
          <ChangelogManagementPanel />
        </>
      )}

      <PublicationConfirm
        visible={appPublishedConfirm}
        published={appPublished}
        onConfirm={handleToggleAppPublished}
        onCancel={() => setAppPublishedConfirm(false)}
        ariaLabel={appPublished ? 'Switch App Store links to coming soon' : 'Make App Store links live'}
        title={appPublished ? 'Switch to Coming Soon?' : 'Make App Store Links Live?'}
        body={appPublished
          ? 'All App Store buttons across the website will show a "Coming Soon" toast instead of linking to the App Store.'
          : 'All App Store buttons across the website will link directly to the App Store. Make sure the app is published first!'}
        confirmLabel={appPublished ? 'Switch to Coming Soon' : 'Go Live'}
      />

      <PublicationConfirm
        visible={androidPublishedConfirm}
        published={androidPublished}
        onConfirm={handleToggleAndroidPublished}
        onCancel={() => setAndroidPublishedConfirm(false)}
        ariaLabel={androidPublished ? 'Switch Google Play links to coming soon' : 'Make Google Play links live'}
        title={androidPublished ? 'Switch to Coming Soon?' : 'Make Google Play Links Live?'}
        body={androidPublished
          ? 'All Google Play buttons across the website will show an Android "Coming Soon" toast instead of linking to the Play Store.'
          : 'All Google Play buttons across the website will link directly to the public Play Store listing. Make sure the app is publicly available on Google Play first!'}
        confirmLabel={androidPublished ? 'Switch to Coming Soon' : 'Go Live'}
      />

      <WaitlistDeleteDialogs
        deleteConfirm={deleteConfirm}
        onCancelDelete={() => setDeleteConfirm(null)}
        onConfirmDelete={deleteEntry}
        bulkDeleteConfirm={bulkDeleteConfirm}
        selectedCount={selectedEntries.size}
        onCancelBulk={() => setBulkDeleteConfirm(false)}
        onConfirmBulk={bulkDeleteEntries}
      />
    </AdminWorkspace>
  );
};

export default AdminConsole;
